import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './ImageZoom.css';

/* Une capture d'app fait ~1900 px de large mais s'affiche dans ~300 px :
   sans agrandissement, rien n'y est lisible. Clic sur la vignette -> plein
   écran, et c'est tout : un second palier de zoom n'apporterait rien, le
   visiteur a déjà celui du navigateur. Reclic (ou Échap) -> fermeture. */

interface ImageZoomProps {
  src: string;
  alt: string;
  /** Classes de la vignette — la mise en page de l'appelant reste maîtresse. */
  className: string;
  /** Libellé du bouton d'agrandissement (i18n de l'appelant). */
  openLabel: string;
  /** Libellé du bouton de fermeture (i18n de l'appelant). */
  closeLabel: string;
}

export default function ImageZoom({
  src, alt, className, openLabel, closeLabel,
}: ImageZoomProps): JSX.Element {
  const [open, setOpen] = useState(false);

  function close(): void {
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return undefined;
    // Le verrou retire la scrollbar : on réserve sa largeur pour que la page
    // dessous ne se décale pas d'un pixel (même compensation qu'ExpandCard).
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${String(scrollbarW)}px`;
    document.body.style.setProperty('--scrollbar-comp', `${String(scrollbarW)}px`);
    document.body.classList.add('ImageZoom-lock');
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('ImageZoom-lock');
      document.body.style.paddingRight = '';
      document.body.style.removeProperty('--scrollbar-comp');
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      {/* L'image porte elle-même le rôle de bouton : l'envelopper casserait
          le dimensionnement de la case (bordure ajustée à l'image). */}
      <img
        className={`${className} ImageZoom__thumb`}
        src={src}
        alt={alt}
        decoding="async"
        role="button"
        tabIndex={0}
        aria-label={openLabel}
        onClick={() => { setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
        }}
      />

      {open && createPortal(
        <div
          className="ImageZoom__overlay"
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={close}
        >
          <img className="ImageZoom__img" src={src} alt={alt} />
          <button
            type="button"
            className="ImageZoom__close"
            aria-label={closeLabel}
            onClick={close}
          >
            <X size={22} strokeWidth={2} />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
